/** @prettier */
//-----------------------------
//	グラフ共通の処理まとめ
//-----------------------------

import {Dispatch, SetStateAction} from 'react';
import {GraphDataRange, GraphFormType} from '../Type/TypeDef';
import {TFunction} from 'i18next';
import {format, utcToZonedTime} from 'date-fns-tz';

/**
 * グラフX軸ラベル作成処理
 * @param props props
 * - xDate 通常日の日付データ
 * - comparisonXDate 比較日の日付データ
 * - dateRange データ取得範囲
 * - graphType グラフの種類
 * - activeComparison 比較状態有効判定
 * - setXAxisLabel グラフのX軸に表示するラベル設定
 * - setComparisonXAxisLabel 週比較の比較データを表示するグラフのX軸に表示するラベル設定
 * - trans 翻訳関数
 */
export const makeXLabel = (props: {
  xDate: string[];
  comparisonXDate: string[];
  dateRange: GraphDataRange;
  graphType: GraphFormType;
  activeComparison: boolean;
  setXAxisLabel: Dispatch<SetStateAction<string[]>>;
  setComparisonXAxisLabel?: Dispatch<SetStateAction<string[]>>;
}) => {
  //比較状態でない場合は通常日の日付データでx軸のラベルを作成する
  if (props.activeComparison === false) {
    props.setXAxisLabel(props.xDate);
  }
  //比較状態かつ日付範囲が1週間の場合
  else if (props.dateRange === 'week') {
    //Trendグラフの場合、通常グラフと比較グラフのx軸のラベルをそれぞれ作成する
    if (props.graphType === 'line' && props.setComparisonXAxisLabel) {
      props.setXAxisLabel(props.xDate);
      props.setComparisonXAxisLabel(props.comparisonXDate);
    }
    //Trendグラフでない場合、通常日と比較日の両方を表示するx軸のラベルを作成する
    else {
      const labelArray = props.xDate.map((item, index) => {
        return `${props.comparisonXDate[index]} , ${item}`;
      });
      props.setXAxisLabel(labelArray);
    }
  }
  //比較状態かつ日付範囲が1週間以外の場合、
  //通常日と比較日でデータが長い方の日付データでx軸のラベルを作成する
  else {
    if (props.xDate.length >= props.comparisonXDate.length) {
      props.setXAxisLabel(props.xDate);
    } else {
      props.setXAxisLabel(props.comparisonXDate);
    }
  }
};

/**
 * X軸で表示する月の多言語対応関数
 * @param date 日付オブジェクト
 * @param timeZone タイムゾーン
 * @param dateFormat 日付フォーマット
 * @param trans 翻訳関数
 * @returns 日付情報
 */
export const translateDate = (
  date: Date | string,
  timeZone: string,
  dateFormat: string,
  trans: TFunction
): string => {
  /**
   * フォーマットされた日付文字列を返す関数。
   * @param month 変換する月（英語）
   * @param trans 翻訳関数
   * @returns 変換した月
   */
  const translateMonth = (month: string, trans: TFunction): string => {
    const monthTranslations: {[key: string]: string} = {
      Jan: trans('graphFunction.january'),
      Feb: trans('graphFunction.february'),
      Mar: trans('graphFunction.march'),
      Apr: trans('graphFunction.april'),
      May: trans('graphFunction.may'),
      Jun: trans('graphFunction.june'),
      Jul: trans('graphFunction.july'),
      Aug: trans('graphFunction.august'),
      Sep: trans('graphFunction.september'),
      Oct: trans('graphFunction.october'),
      Nov: trans('graphFunction.november'),
      Dec: trans('graphFunction.december'),
    };

    return monthTranslations[month] || month;
  };

  // タイムゾーンに基づいて日付を変換
  const zonedDate = utcToZonedTime(date, timeZone);
  // 日付をフォーマット
  const formattedDate = format(zonedDate, dateFormat);

  //月があるなら変換
  const [month, day] = formattedDate.split('.') ?? [];
  const translatedMonth = month ? translateMonth(month, trans) : '';

  // 翻訳された月で日付を再構成
  return formattedDate.replace(month, translatedMonth);
};
